import React from 'react'
import { ClipboardPlus, UserCheck, GitMerge, Users, ExternalLink, AlertTriangle } from 'lucide-react'
import { router, Link } from '@inertiajs/react'
import DashboardLayout from '../layouts/DashboardLayout'
import { cn } from '../lib/utils'

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'

// Reception MERGE QUEUE (rec #3). Every self-registration awaiting review in one place, each with
// the existing patient it most likely duplicates (deterministic ID → phone → name) and a one-tap
// decision: Approve as new / Merge into existing / Add as dependant. Stops duplicate accounts
// piling up at volume. The actions hit the same audited endpoints the patient profile uses.
export default function ReviewQueue({ rows = [], total = 0 }) {
  const act = (id, path, body = {}) => router.post(`/patients/${id}/${path}`, body, { preserveScroll: true })

  const approveNew = (r) => {
    if (confirm(`Approve "${r.name}" as a genuinely NEW patient? This creates a new account and promotes their form.`)) act(r.id, 'confirm_new')
  }
  const mergeInto = (r) => {
    if (r.likely_match && confirm(`Merge "${r.name}" INTO existing patient ${r.likely_match.name}${r.likely_match.account_code ? ` (${r.likely_match.account_code})` : ''}? The self-registration is absorbed — no new account.`)) {
      act(r.id, 'merge_into', { target_id: r.likely_match.id })
    }
  }
  const addDependant = (r) => {
    if (r.likely_match && confirm(`Add "${r.name}" as a DEPENDANT on ${r.likely_match.name}'s account? Keeps a separate record, links to the family account, no new account.`)) {
      act(r.id, 'link_as_dependant', { target_id: r.likely_match.id })
    }
  }

  const btn = 'inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-40'

  return (
    <DashboardLayout>
      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-primary"><ClipboardPlus size={18} className="text-white" /></div>
        <div>
          <h1 className="text-xl font-semibold text-brand-ink">Review queue</h1>
          <p className="text-sm text-brand-muted">{total} self-registration{total === 1 ? '' : 's'} awaiting review. Confirm each is new, or link it to the existing patient it matches — before a duplicate account is created.</p>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-brand-border bg-white px-6 py-12 text-center text-sm text-brand-muted">
          Nothing to review — every self-registration has been triaged. 🎉
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.id} className="rounded-xl border border-brand-border bg-white p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Link href={`/patients/${r.id}`} className="font-semibold text-brand-ink hover:text-brand-primary">{r.name}</Link>
                    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700">Unverified</span>
                  </div>
                  <p className="mt-0.5 text-xs text-brand-muted">
                    {r.phone || 'no phone'}{r.id_number ? ` · ID ${r.id_number}` : ''} · registered {fmtDate(r.registered_at)}
                    {r.intake_count > 0 ? ` · ${r.intake_count} form${r.intake_count === 1 ? '' : 's'} on file` : ''}
                  </p>
                </div>
                <Link href={`/patients/${r.id}`} className="inline-flex items-center gap-1 text-xs font-medium text-brand-primary hover:underline">
                  View profile <ExternalLink size={12} />
                </Link>
              </div>

              {r.likely_match ? (
                <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg bg-amber-50 px-3 py-2">
                  <AlertTriangle size={14} className="text-amber-600" />
                  <span className="text-xs text-amber-800">
                    Likely duplicate of <Link href={`/patients/${r.likely_match.id}`} className="font-semibold hover:underline">{r.likely_match.name}</Link>
                    {r.likely_match.account_code ? ` (${r.likely_match.account_code})` : ' (no account yet)'} — <span className="font-medium">{r.likely_match.reason}</span>
                  </span>
                </div>
              ) : (
                <p className="mt-3 text-xs text-brand-muted">No existing match found — likely a genuinely new patient.</p>
              )}

              <div className="mt-3 flex flex-wrap gap-2">
                <button type="button" onClick={() => approveNew(r)} className={cn(btn, 'border-emerald-300 text-emerald-700 hover:bg-emerald-50')}>
                  <UserCheck size={13} /> Approve as new
                </button>
                <button type="button" onClick={() => mergeInto(r)} disabled={!r.likely_match} className={cn(btn, 'border-brand-primary text-brand-primary hover:bg-brand-surface')}>
                  <GitMerge size={13} /> Merge into existing
                </button>
                <button type="button" onClick={() => addDependant(r)} disabled={!r.likely_match} className={cn(btn, 'border-brand-border text-brand-ink hover:bg-brand-surface')}>
                  <Users size={13} /> Add as dependant
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  )
}
